import './MatchChatView.css'

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import { session, matches, messages, agentChat } from '../store'

import { useRequireAuthentication } from '../hooks/useRequireAuthentication'
import { useRequireOnboarding } from '../hooks/useRequireOnboarding'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { Button } from '../components/Button'

export function MatchChatView() {
  useRequireAuthentication()
  useRequireOnboarding()

  const { matchId } = useParams<{ matchId: string }>()
  const navigate = useNavigate()
  const [, setBreadcrumbs] = useBreadcrumbs()

  const authStatus = useAtomValue(session.sessionStatusAtom)
  const currentSession = useAtomValue(session.sessionAtom)
  const matchList = useAtomValue(matches.matchesAtom)
  const messageList = useAtomValue(messages.messagesAtom)
  const isLoading = useAtomValue(messages.messagesIsLoadingAtom)
  const sendLoading = useAtomValue(messages.messagesSendIsLoadingAtom)
  const error = useAtomValue(messages.messagesErrorAtom)

  const fetchMatches = useSetAtom(matches.fetchMatchesAtom)
  const fetchMessages = useSetAtom(messages.fetchMessagesAtom)
  const sendMessage = useSetAtom(messages.sendMessageAtom)
  const clearMessages = useSetAtom(messages.clearMessagesAtom)
  const unmatch = useSetAtom(matches.unmatchAtom)
  const openChat = useSetAtom(agentChat.agentChatOpenAtom)

  const [draft, setDraft] = useState('')
  const [actionPending, setActionPending] = useState(false)
  const [introductionEnded, setIntroductionEnded] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  const match = matchList.find((m) => m.id === matchId)
  const ownProfileId = currentSession?.user.profile?.id

  useEffect(() => {
    if (authStatus === 'authenticated' && matchList.length === 0 && !introductionEnded) {
      fetchMatches()
    }
  }, [authStatus, matchList.length, fetchMatches, introductionEnded])

  useEffect(() => {
    if (!matchId || authStatus !== 'authenticated') return
    fetchMessages(matchId)
    return () => {
      clearMessages()
    }
  }, [matchId, authStatus, fetchMessages, clearMessages])

  useEffect(() => {
    if (!match) return
    setBreadcrumbs([
      { label: 'Matches', path: '/matches' },
      { label: match.profile.name, path: `/matches/${match.profile.id}` },
      { label: 'Chat' },
    ])
  }, [match, setBreadcrumbs])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messageList.length])

  const handleSend = async () => {
    if (!matchId || !draft.trim() || sendLoading) return
    const body = draft.trim()
    setDraft('')
    const result = await sendMessage({ matchId, body })
    if (!result.success) {
      setDraft(body)
    }
  }

  const handleUnmatch = async () => {
    if (!matchId || actionPending) return
    setActionPending(true)
    const result = await unmatch(matchId)
    setActionPending(false)
    clearMessages()
    if (result.success && result.openRheya) {
      openChat({ mode: 'unmatch' })
    }
    if (result.success || result.ended) {
      setIntroductionEnded(true)
      navigate('/matches')
    }
  }

  if (authStatus !== 'authenticated') {
    return <div className="match-chat-view__status">Loading…</div>
  }

  if (introductionEnded || (!match && matchList.length > 0)) {
    return (
      <div className="match-chat-view">
        <p className="match-chat-view__status">This introduction has ended.</p>
        <Button kind="ghost" text="Back to matches" onClick={() => navigate('/matches')} />
      </div>
    )
  }

  if (match && match.status !== 'connected') {
    return (
      <div className="match-chat-view">
        <p className="match-chat-view__status">
          You need mutual interest with {match.profile.name} before chatting.
        </p>
        <Button
          kind="ghost"
          text="View profile"
          onClick={() => navigate(`/matches/${match.profile.id}`)}
        />
      </div>
    )
  }

  return (
    <div className="match-chat-view">
      <header className="match-chat-view__header">
        <h1 className="match-chat-view__title">
          {match ? `Chat with ${match.profile.name}` : 'Chat'}
        </h1>
        {match && (
          <Button
            kind="ghost"
            text={actionPending ? '…' : 'Unmatch'}
            onClick={handleUnmatch}
            disabled={actionPending}
          />
        )}
      </header>

      <div className="match-chat-view__messages" role="log" aria-live="polite">
        {isLoading && <p className="match-chat-view__status">Loading messages…</p>}
        {error && <p className="match-chat-view__error">{error}</p>}
        {!isLoading && messageList.length === 0 && (
          <p className="match-chat-view__status">
            Say hello — this is the start of your conversation.
          </p>
        )}
        {messageList.map((message) => {
          const isMine = message.senderProfileId === ownProfileId
          return (
            <div
              key={message.id}
              className={`match-chat-view__bubble ${isMine ? 'match-chat-view__bubble--mine' : 'match-chat-view__bubble--theirs'}`}
            >
              <p className="match-chat-view__body">{message.body}</p>
              <time className="match-chat-view__time" dateTime={message.createdAt}>
                {new Date(message.createdAt).toLocaleString()}
              </time>
            </div>
          )
        })}
        <div ref={bottomRef} />
      </div>

      <form
        className="match-chat-view__composer"
        onSubmit={(event) => {
          event.preventDefault()
          handleSend()
        }}
      >
        <input
          className="match-chat-view__input"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Write a message…"
          maxLength={2000}
          disabled={sendLoading || !matchId}
          aria-label="Message"
        />
        <Button
          type="submit"
          kind="primary"
          text={sendLoading ? 'Sending…' : 'Send'}
          disabled={sendLoading || !draft.trim()}
        />
      </form>
    </div>
  )
}
